// Scheduling Conflict Service
// Detects and prevents double-booking for sales reps

import { meetingService } from '../utils/supabaseData';
import { logger } from '../utils/logger';
import { supabase } from '../utils/supabase';

export const schedulingConflictService = {
  /**
   * Get sales rep's meetings for a specific date
   * @param {string} salesRepId - Sales rep UUID
   * @param {string} dateString - Date in YYYY-MM-DD format
   * @returns {Promise<Array>} Array of meetings with start/end times
   */
  async getSalesRepMeetingsForDate(salesRepId, dateString) {
    try {
      logger.debug('SchedulingConflict: Getting sales rep meetings', {
        salesRepId,
        date: dateString
      });

      // Get all meetings for the sales rep
      const allMeetings = await meetingService.getAllMeetings(salesRepId, 'sales');

      // Filter for the specific date and active statuses
      const dateMeetings = allMeetings.filter(meeting => {
        const meetingDate = meeting.meeting_date || meeting.date;
        const status = meeting.status;

        // Only include pending, confirmed meetings (not cancelled/completed)
        const isActiveStatus = ['pending', 'confirmed'].includes(status);

        return meetingDate === dateString && isActiveStatus;
      });

      if (dateMeetings.length === 0) {
        return [];
      }

      // Collect all unique customer IDs
      const customerIds = [...new Set(dateMeetings.map(m => m.customer_id).filter(Boolean))];

      // Fetch customer names from user_profiles
      let customerMap = {};
      if (customerIds.length > 0) {
        const { data: customers, error: customerError } = await supabase
          .from('user_profiles')
          .select('id, first_name, last_name')
          .in('id', customerIds);

        if (customerError) {
          logger.warn('SchedulingConflict: Error fetching customer names', {
            error: customerError.message
          });
        } else if (customers) {
          customers.forEach(customer => {
            const fullName = `${customer.first_name || ''} ${customer.last_name || ''}`.trim();
            customerMap[customer.id] = fullName || 'Unknown Customer';
          });
        }
      }

      // Enhance each meeting with customer name
      const enhancedMeetings = dateMeetings.map(meeting => {
        let customerName = 'Unknown Customer';

        if (meeting.customer_id && customerMap[meeting.customer_id]) {
          customerName = customerMap[meeting.customer_id];
        } else if (meeting.customer && typeof meeting.customer === 'object') {
          // Fallback: if customer object is already present
          const firstName = meeting.customer.first_name || '';
          const lastName = meeting.customer.last_name || '';
          customerName = `${firstName} ${lastName}`.trim() || customerName;
        } else if (meeting.customer_name) {
          customerName = meeting.customer_name;
        } else if (meeting.customer) {
          customerName = meeting.customer;
        }

        return {
          ...meeting,
          customer_name: customerName
        };
      });

      logger.debug('SchedulingConflict: Found meetings for date', {
        count: enhancedMeetings.length,
        meetings: enhancedMeetings.map(m => ({
          time: m.start_time || m.meeting_time || m.time,
          endTime: m.end_time,
          customer: m.customer_name,
          customerId: m.customer_id,
          status: m.status
        }))
      });

      return enhancedMeetings;
    } catch (error) {
      logger.error('SchedulingConflict: Error getting meetings', {
        error: error.message
      });
      return [];
    }
  },

  /**
   * Check if a specific time slot conflicts with existing meetings
   * @param {string} timeSlot - Time in format "HH:MM AM/PM"
   * @param {Array} existingMeetings - Array of existing meetings
   * @param {number} meetingDuration - Duration in minutes (default 60)
   * @returns {Object} { hasConflict: boolean, conflictingMeeting: Object|null }
   */
  checkTimeSlotConflict(timeSlot, existingMeetings, meetingDuration = 60) {
    if (!existingMeetings || existingMeetings.length === 0) {
      return { hasConflict: false, conflictingMeeting: null };
    }

    // Convert time slot to 24-hour format for comparison
    const slotStart = this._convertTo24Hour(timeSlot);
    const slotEnd = this._addMinutes(slotStart, meetingDuration);

    for (const meeting of existingMeetings) {
      // Use start_time from database, fallback to other possible field names
      const meetingTime = meeting.start_time || meeting.meeting_time || meeting.time;

      if (!meetingTime) {
        logger.warn('SchedulingConflict: Meeting has no time field', { meeting });
        continue;
      }

      const meetingStart = this._convertTo24Hour(meetingTime);

      // Use end_time if available, otherwise assume 60-minute meeting
      let meetingEnd;
      if (meeting.end_time) {
        meetingEnd = this._convertTo24Hour(meeting.end_time);
      } else {
        const meetingDurationMinutes = 60;
        meetingEnd = this._addMinutes(meetingStart, meetingDurationMinutes);
      }

      // Check for overlap: (StartA < EndB) and (EndA > StartB)
      if (slotStart < meetingEnd && slotEnd > meetingStart) {
        logger.debug('SchedulingConflict: Conflict detected', {
          proposedTime: timeSlot,
          slotStart,
          slotEnd,
          conflictingMeeting: {
            time: meetingTime,
            meetingStart,
            meetingEnd,
            customer: meeting.customer_name || meeting.customer
          }
        });

        return {
          hasConflict: true,
          conflictingMeeting: meeting
        };
      }
    }

    return { hasConflict: false, conflictingMeeting: null };
  },

  /**
   * Filter time slots to show only non-conflicting options
   * @param {Array} timeSlots - Array of time slot strings
   * @param {Array} existingMeetings - Array of existing meetings
   * @param {number} meetingDuration - Duration in minutes
   * @returns {Object} { available: Array, conflicting: Array }
   */
  filterTimeSlots(timeSlots, existingMeetings, meetingDuration = 60) {
    const available = [];
    const conflicting = [];

    timeSlots.forEach(slot => {
      const { hasConflict, conflictingMeeting } = this.checkTimeSlotConflict(
        slot,
        existingMeetings,
        meetingDuration
      );

      if (hasConflict) {
        conflicting.push({
          time: slot,
          conflictsWith: conflictingMeeting
        });
      } else {
        available.push(slot);
      }
    });

    logger.debug('SchedulingConflict: Filtered time slots', {
      total: timeSlots.length,
      available: available.length,
      conflicting: conflicting.length
    });

    return { available, conflicting };
  },

  /**
   * Get available time slots merged with customer availability and sales rep schedule
   * @param {string} salesRepId - Sales rep UUID
   * @param {string} dateString - Date in YYYY-MM-DD format
   * @param {Array} customerAvailableSlots - Customer's available time slots
   * @returns {Promise<Object>} { available: Array, conflicting: Array, salesRepMeetings: Array }
   */
  async getMergedAvailability(salesRepId, dateString, customerAvailableSlots = []) {
    try {
      // Get sales rep's existing meetings for this date
      const salesRepMeetings = await this.getSalesRepMeetingsForDate(salesRepId, dateString);

      // If no customer slots provided, use default lunch time slots
      const slotsToCheck = customerAvailableSlots.length > 0
        ? customerAvailableSlots
        : [
            '11:00 AM', '11:30 AM', '12:00 PM', '12:30 PM',
            '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM'
          ];

      // Filter slots based on conflicts
      const { available, conflicting } = this.filterTimeSlots(
        slotsToCheck,
        salesRepMeetings
      );

      return {
        available,
        conflicting,
        salesRepMeetings
      };
    } catch (error) {
      logger.error('SchedulingConflict: Error getting merged availability', {
        error: error.message
      });
      return {
        available: customerAvailableSlots,
        conflicting: [],
        salesRepMeetings: []
      };
    }
  },

  /**
   * Helper: Convert time to 24-hour format (HH:MM)
   * Handles both 12-hour format ("2:00 PM") and 24-hour format ("14:00:00")
   */
  _convertTo24Hour(timeString) {
    if (!timeString) return null;

    // If already in 24-hour format (contains : but no AM/PM)
    if (timeString.includes(':') && !timeString.includes('AM') && !timeString.includes('PM')) {
      // Split and take only HH:MM (remove seconds if present)
      const parts = timeString.split(':');
      const hours = parts[0].padStart(2, '0');
      const minutes = parts[1].padStart(2, '0');
      return `${hours}:${minutes}`;
    }

    // Handle 12-hour format with AM/PM
    const [time, modifier] = timeString.split(' ');
    let [hours, minutes] = time.split(':');

    if (hours === '12') {
      hours = '00';
    }
    if (modifier === 'PM') {
      hours = (parseInt(hours, 10) + 12).toString();
    }

    return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
  },

  /**
   * Helper: Add minutes to a time string
   */
  _addMinutes(timeString, minutes) {
    const [hours, mins] = timeString.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, mins, 0, 0);
    date.setMinutes(date.getMinutes() + minutes);

    const newHours = String(date.getHours()).padStart(2, '0');
    const newMins = String(date.getMinutes()).padStart(2, '0');

    return `${newHours}:${newMins}`;
  }
};

export default schedulingConflictService;
